/**
 * TanStack Query hooks for Staffing Concerns (RP-34) and a plan's account team (RP-34.1).
 *
 * A Concern write can change the plan itself (a Blocking one returns it; resolving that one
 * picks it back up) and the Rejected Plans report, so every mutation invalidates plans and
 * that report. Concern keys sit under the plan prefix, so plan invalidation refreshes them.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { send, useInvalidatePlans } from "@/hooks/use-resource-plans";
import { planRejectionKeys, resourcePlanKeys } from "@/lib/query";
import type {
  PlanAccountTeamView,
  PlanConcernListResponse,
  PlanConcernType,
} from "@/schemas/plan-concerns";

export function usePlanConcerns(planId: string, enabled = true) {
  return useQuery({
    queryKey: resourcePlanKeys.concerns(planId),
    queryFn: () => send<PlanConcernListResponse>(`/api/plans/${planId}/concerns`, "GET"),
    enabled,
  });
}

export function usePlanAccountTeam(planId: string, enabled: boolean) {
  return useQuery({
    queryKey: resourcePlanKeys.accountTeam(planId),
    queryFn: () => send<PlanAccountTeamView>(`/api/plans/${planId}/account-team`, "GET"),
    enabled,
  });
}

function useConcernMutation<TInput>(
  url: (input: TInput) => string,
  body: (input: TInput) => unknown,
  success: (input: TInput) => string,
  failure: string,
) {
  const invalidatePlans = useInvalidatePlans();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => send<PlanConcernListResponse>(url(input), "POST", body(input)),
    onSuccess: (_data, input) => {
      invalidatePlans();
      qc.invalidateQueries({ queryKey: planRejectionKeys.all });
      toast.success(success(input));
    },
    onError: (err: Error) => toast.error(err.message || failure),
  });
}

export interface RaisePlanConcernInput {
  type: PlanConcernType;
  message: string;
  planLineId: string | null;
  isBlocking: boolean;
}

export function useRaisePlanConcern(planId: string) {
  return useConcernMutation<RaisePlanConcernInput>(
    () => `/api/plans/${planId}/concerns`,
    (input) => input,
    (input) =>
      input.isBlocking
        ? "Concern raised and plan rejected"
        : "Concern raised — the account team has been notified",
    "Failed to raise the Concern",
  );
}

export function useAcknowledgePlanConcern(planId: string) {
  return useConcernMutation<{ concernId: string; note: string }>(
    (input) => `/api/plans/${planId}/concerns/${input.concernId}/acknowledge`,
    (input) => ({ note: input.note }),
    () => "Concern acknowledged",
    "Failed to acknowledge the Concern",
  );
}

export function useResolvePlanConcern(planId: string) {
  return useConcernMutation<{ concernId: string; note: string }>(
    (input) => `/api/plans/${planId}/concerns/${input.concernId}/resolve`,
    (input) => ({ note: input.note }),
    () => "Concern resolved",
    "Failed to resolve the Concern",
  );
}
